/**
 * The audit record and the purge, for internal program audits and FDR
 * assessments alike (operator order 2026-08-29):
 *   1. Download the complete record: one zip with the notice, the universes
 *      and their test results, the fieldwork, the draft, the responses and
 *      evidence, the final, every email as sent or received, and the audit
 *      log, with an index and a hash per file.
 *   2. Purge and close: unlocked once the record has been downloaded (when
 *      a final report exists), confirmed by typing PURGE; the destruction
 *      certificate is shown, downloadable, and emailed to the person who
 *      purged and the compliance officers.
 * Plain language, no engineering words on screen.
 */
import React, { useState } from 'react';

const API = process.env.REACT_APP_API_URL;
const fmt = (d) => (d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

// The words describe what this program's record holds and what the purge
// destroys. The defaults fit the universe based programs (ODAG, CDAG, SNPCC,
// PDA); a program without universes passes its own and hides the universe erase.
const DEFAULT_WORDING = {
  record: 'One file: engagement notice, universes and test results, fieldwork with every case, draft report, rebuttals with evidence and dispositions, final report, every email as sent and received, the audit log, and an index with a hash for each file.',
  purge: 'Erases every universe, sample file, evidence file and case identifier, verifies the erasure, issues the destruction certificate, and emails it to you and the compliance officers.',
};
export default function AuditRecordPanel({ g, jfetch, onChanged, readOnly = false, purgeOpen = false, setPurgeOpen = () => {}, allowUnansweredPrompt = true, wording = DEFAULT_WORDING, showUniverseErase = true }) {
  const [downloading, setDownloading] = useState(false);
  const [eraseOpen, setEraseOpen] = useState(false);
  const [eraseText, setEraseText] = useState('');
  const [erasing, setErasing] = useState(false);
  const [eraseDone, setEraseDone] = useState(null);
  const eraseNow = async () => {
    setErasing(true); setErr('');
    try {
      const d = await jfetch(`/engagements/${g.id}/universes/erase`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ confirm: 'ERASE' }) });
      setEraseDone({ files: d.destroyed_files, samples: d.samples_kept }); setEraseOpen(false); setEraseText(''); onChanged();
    } catch (e) { setErr(e.message); }
    setErasing(false);
  };
  const [confirmText, setConfirmText] = useState('');
  const [closing, setClosing] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  const [savedAs, setSavedAs] = useState('');
  const [openWork, setOpenWork] = useState('');
  const closed = g.status === 'closed';
  const needsDownload = !!g.report_final_at && !g.record_downloaded_at;

  const download = async () => {
    setDownloading(true); setErr('');
    try {
      const r = await fetch(`${API}/api/fdr/engagements/${g.id}/record.zip`, { credentials: 'include' });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.message || 'Could not compile the audit record.'); }
      const blob = await r.blob();
      const name = (r.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/)?.[1] || 'Audit record.zip';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      setSavedAs(`${name} (${Math.max(1, Math.round(blob.size / 1024))} KB)`);
      onChanged && onChanged();
    } catch (e) { setErr(e.message); }
    setDownloading(false);
  };

  const purge = async (allow) => {
    setClosing(true); setErr('');
    try {
      const d = await jfetch(`/engagements/${g.id}/complete`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ allow_unanswered: allow, confirm: confirmText.trim().toUpperCase() }),
      });
      setResult(d);
      setPurgeOpen(false); setOpenWork('');
      onChanged && onChanged();
    } catch (e) {
      // Open work is stated inline, never in a native dialog: the person
      // reads what is unfinished and decides to close anyway or go back.
      if (allowUnansweredPrompt && /no (outcome|verdict) yet/.test(e.message)) setOpenWork(e.message);
      else setErr(e.message);
    }
    setClosing(false);
  };

  const cert = result?.certificate || g.phi_purge_certificate || null;

  return (
    <div className="mt-4 bg-white border border-slate-200 rounded-2xl px-5 py-4">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Audit record</span>
        <span className="text-[11.5px] text-slate-600">Download the complete record, then purge the working material and close. Outcomes, reports, emails and hashes remain; member data does not.</span>
      </div>

      <div className="mt-3 grid md:grid-cols-2 gap-3">
        <div className={`rounded-xl px-4 py-3 ring-1 ${g.record_downloaded_at ? 'bg-emerald-50/60 ring-emerald-200' : 'bg-slate-50 ring-slate-200'}`}>
          <div className="text-[11px] font-bold text-slate-700">1 · The complete record</div>
          <div className="mt-1 text-[11.5px] text-slate-600">{wording.record}</div>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <button type="button" onClick={download} disabled={downloading}
              className="px-3.5 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold disabled:opacity-50">
              {downloading ? 'Compiling…' : 'Download the complete record (.zip)'}
            </button>
            {g.record_downloaded_at ? (
              <span className="text-[11px] text-emerald-800">Downloaded {fmt(g.record_downloaded_at)}{g.record_download_count > 1 ? ` · ${g.record_download_count} times` : ''}{g.record_sha256 ? ` · sha256 ${String(g.record_sha256).slice(0, 12)}…` : ''}</span>
            ) : (
              <span className="text-[11px] text-slate-400">Not downloaded yet</span>
            )}
          </div>
          {savedAs && <div className="mt-1.5 text-[11px] font-semibold text-emerald-800">Saved to your Downloads as {savedAs}. Keep it where the organization keeps audit records before purging.</div>}
        </div>

        <div className={`rounded-xl px-4 py-3 ring-1 ${closed ? 'bg-emerald-50/60 ring-emerald-200' : needsDownload ? 'bg-slate-50 ring-slate-200 opacity-80' : 'bg-rose-50/50 ring-rose-200'}`}>
          <div className="text-[11px] font-bold text-slate-700">2 · Purge and close</div>
          {closed ? (
            <div className="mt-1 text-[11.5px] text-emerald-800">
              Purged and closed {fmt(g.closed_at)}. {cert?.phi_working_material ? `${cert.phi_working_material}.` : ''}
              {result?.certificate_mail ? <> Certificate emailed to {[...(result.certificate_mail.to || []), ...(result.certificate_mail.cc || [])].join(', ')}.</> : null}
              <div className="mt-1.5">
                <a href={`${API}/api/fdr/engagements/${g.id}/purge-certificate.txt`} className="text-[11px] font-semibold text-orange-700 hover:text-orange-900 no-underline">Download the destruction certificate</a>
              </div>
            </div>
          ) : (
            <>
              <div className="mt-1 text-[11.5px] text-slate-600">
                {needsDownload
                  ? 'Unlocks once the complete record has been downloaded.'
                  : wording.purge}
              </div>
              {!readOnly && !needsDownload && (
                !purgeOpen ? (
                  <button type="button" onClick={() => { setPurgeOpen(true); setConfirmText(''); }}
                    className="mt-2 px-3.5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold">
                    Purge and close
                  </button>
                ) : (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Type PURGE to confirm"
                      className="text-[12px] border border-rose-200 rounded-lg px-2.5 py-1.5 bg-white w-48" />
                    <button type="button" onClick={() => purge(false)} disabled={closing || confirmText.trim().toUpperCase() !== 'PURGE'}
                      className="px-3.5 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold disabled:opacity-40">
                      {closing ? 'Purging…' : 'Purge now'}
                    </button>
                    <button type="button" onClick={() => { setPurgeOpen(false); setOpenWork(''); }} className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-700">Cancel</button>
                    {openWork && (
                      <div className="w-full mt-1 rounded-lg bg-amber-50 ring-1 ring-amber-200 px-3 py-2 text-[11.5px] text-amber-900">
                        {openWork}
                        <button type="button" onClick={() => purge(true)} disabled={closing}
                          className="ml-2 px-2.5 py-1 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-[11px] font-semibold disabled:opacity-40">
                          Close anyway
                        </button>
                      </div>
                    )}
                  </div>
                )
              )}
            </>
          )}
        </div>
      </div>
      {!closed && showUniverseErase && (
        <div className="mt-3 rounded-xl px-4 py-3 ring-1 bg-white ring-slate-200">
          <div className="text-[11px] font-bold text-slate-700">Erase the universes now</div>
          <div className="mt-1 text-[11.5px] text-slate-600">
            Destroys every universe file on every table — the bulk of the member data — and keeps the selected samples you are working, the universe results, the error and score reports, and the attestation. Nothing else changes; what you erase cannot be brought back.
          </div>
          {eraseDone && (
            <div className="mt-1.5 text-[11.5px] text-emerald-800">Erased {eraseDone.files} universe file{eraseDone.files === 1 ? '' : 's'}; {eraseDone.samples} selected case{eraseDone.samples === 1 ? '' : 's'} kept. Attestation recorded.</div>
          )}
          {!readOnly && !eraseOpen && (
            <button type="button" onClick={() => { setEraseOpen(true); setEraseText(''); }}
              className="mt-2 px-3.5 py-2 rounded-xl bg-white ring-1 ring-rose-300 text-rose-700 hover:bg-rose-50 text-xs font-semibold">
              Erase the universes
            </button>
          )}
          {eraseOpen && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <input value={eraseText} onChange={(e) => setEraseText(e.target.value)} placeholder="Type ERASE to confirm"
                className="text-[12px] border border-rose-200 rounded-lg px-2.5 py-1.5 bg-white w-48" />
              <button type="button" onClick={eraseNow} disabled={erasing || eraseText.trim().toUpperCase() !== 'ERASE'}
                className="px-3.5 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold disabled:opacity-40">
                {erasing ? 'Erasing…' : 'Erase now'}
              </button>
              <button type="button" onClick={() => setEraseOpen(false)} className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-700">Cancel</button>
            </div>
          )}
        </div>
      )}
      {err && <div className="mt-2 text-[11.5px] font-semibold text-rose-600">{err}</div>}
    </div>
  );
}
